import { useEffect, useState } from 'react';

/** Total founding-cohort seats — fallback until the worker reports its own total. */
export const SEATS_TOTAL = 35;

/** Early-access form endpoint; the live seat count lives at `${API_URL}/count`. */
const API_URL = import.meta.env.VITE_EARLY_ACCESS_API_URL ?? '';
const COUNT_URL = API_URL ? `${API_URL.replace(/\/$/, '')}/count` : '';

/** `loading` until the first fetch resolves, then `loaded` or `unavailable`. */
export type FoundingSeatsStatus = 'loading' | 'loaded' | 'unavailable';

export interface FoundingSeats {
  /** Seats remaining; null until (and unless) a live count loads. */
  seatsRemaining: number | null;
  /** Total seats in the founding cohort (worker-provided once loaded). */
  seatsTotal: number;
  /** True only when a live count loaded and reports zero remaining. */
  soldOut: boolean;
  /** Fetch lifecycle — lets callers avoid asserting availability before it loads. */
  status: FoundingSeatsStatus;
  /** Convenience flag: a real count is available (`status === 'loaded'`). */
  hasLiveCount: boolean;
}

/**
 * Reads the live founding-seat count from the early-access worker (`${API_URL}/count`),
 * using the worker's own `total`. Non-blocking: exposes an explicit `status` so callers
 * render neutral copy (rather than assuming all seats are open) when the count is still
 * loading or the endpoint is unset/unreachable.
 */
export function useFoundingSeats(): FoundingSeats {
  const [seatsRemaining, setSeatsRemaining] = useState<number | null>(null);
  const [seatsTotal, setSeatsTotal] = useState<number>(SEATS_TOTAL);
  const [status, setStatus] = useState<FoundingSeatsStatus>(COUNT_URL ? 'loading' : 'unavailable');

  useEffect(() => {
    if (!COUNT_URL) return undefined;
    const controller = new AbortController();
    fetch(COUNT_URL, { signal: controller.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { ok?: boolean; remaining?: number; total?: number } | null) => {
        if (data?.ok && typeof data.remaining === 'number') {
          // Trust the worker's own total (falls back to the local default).
          const total = typeof data.total === 'number' ? data.total : SEATS_TOTAL;
          setSeatsTotal(total);
          setSeatsRemaining(Math.max(0, Math.min(total, data.remaining)));
          setStatus('loaded');
        } else {
          setStatus('unavailable');
        }
      })
      .catch(() => {
        // Ignore aborts from unmount; treat genuine failures as unavailable.
        if (!controller.signal.aborted) setStatus('unavailable');
      });
    return () => controller.abort();
  }, []);

  return {
    seatsRemaining,
    seatsTotal,
    soldOut: status === 'loaded' && seatsRemaining === 0,
    status,
    hasLiveCount: status === 'loaded',
  };
}
